import React, { useState, useRef, useEffect, useCallback } from 'react';
import { triggerHaptic } from '../utils/nativeBridge';

const PULL_THRESHOLD = 65;

const PullToRefresh = ({ onRefresh, children, disabled = false }) => {
  const [pullDistance, setPullDistance] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const startYRef = useRef(0);
  const isPullingRef = useRef(false);
  const hasTriggeredHapticRef = useRef(false);

  const handleTouchStart = (e) => {
    if (disabled || isRefreshing) return;
    const scrollTop = window.scrollY || document.documentElement.scrollTop || 0;
    if (scrollTop <= 1) {
      startYRef.current = e.touches[0].clientY;
      isPullingRef.current = true;
      hasTriggeredHapticRef.current = false;
    }
  };

  const handleTouchMove = (e) => {
    if (!isPullingRef.current || disabled || isRefreshing) return;
    const currentY = e.touches[0].clientY;
    const diff = currentY - startYRef.current;

    const scrollTop = window.scrollY || document.documentElement.scrollTop || 0;
    if (scrollTop > 1) {
      isPullingRef.current = false;
      setPullDistance(0);
      return;
    }

    if (diff > 0) {
      // Elastic rubber-banding formula
      const elasticDistance = Math.min(Math.pow(diff, 0.85) * 1.5, 95);
      setPullDistance(elasticDistance);

      if (elasticDistance >= PULL_THRESHOLD && !hasTriggeredHapticRef.current) {
        hasTriggeredHapticRef.current = true;
        triggerHaptic('light');
      } else if (elasticDistance < PULL_THRESHOLD && hasTriggeredHapticRef.current) {
        hasTriggeredHapticRef.current = false;
      }
    } else {
      setPullDistance(0);
    }
  };

  const handleTouchEnd = async () => {
    if (!isPullingRef.current || disabled) return;
    isPullingRef.current = false;

    if (pullDistance >= PULL_THRESHOLD && typeof onRefresh === 'function') {
      setIsRefreshing(true);
      setPullDistance(52); // Keep spinner visible during refresh
      triggerHaptic('medium');

      try {
        await onRefresh();
      } catch (err) {
        console.warn('PullToRefresh error:', err);
      } finally {
        setIsRefreshing(false);
        setPullDistance(0);
      }
    } else {
      setPullDistance(0);
    }
  };

  const progress = Math.min(pullDistance / PULL_THRESHOLD, 1);
  const rotation = progress * 180;

  return (
    <div
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      className="relative w-full"
    >
      {/* Pull Indicator Area */}
      <div
        className="w-full flex items-center justify-center overflow-hidden transition-all pointer-events-none"
        style={{
          height: `${pullDistance}px`,
          transition: isPullingRef.current ? 'none' : 'height 0.25s cubic-bezier(0.2, 0.9, 0.3, 1)',
        }}
      >
        <div
          className={`w-9 h-9 rounded-full flex items-center justify-center shadow-md transition-all ${
            pullDistance >= PULL_THRESHOLD || isRefreshing
              ? 'bg-sky-500 text-white scale-105 shadow-sky-500/30'
              : 'bg-white dark:bg-zinc-800 text-zinc-600 dark:text-zinc-300 border border-zinc-200 dark:border-zinc-700'
          }`}
        >
          {isRefreshing ? (
            <i className="fas fa-spinner fa-spin text-sm" />
          ) : (
            <i
              className="fas fa-arrow-down text-xs transition-transform duration-100"
              style={{ transform: `rotate(${rotation}deg)` }}
            />
          )}
        </div>
      </div>

      {children}
    </div>
  );
};

export default PullToRefresh;
